import React, { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { cmsService } from '@services/api'
import { useAuthStore } from '@store/authStore'
import {
  Truck,
  Plus,
  Search,
  Filter,
  Wrench,
  Eye,
  Edit,
  Trash2,
  Download,
  Car,
  Phone,
  Mail,
  CheckCircle,
  XCircle,
  AlertCircle,
  User,
  MapPin,
  Calendar,
  Fuel
} from 'lucide-react'
import {
  VStack,
  HStack,
  Text,
  Heading,
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  Card,
  CardBody,
  CardHeader,
  Center,
  Flex,
  useColorModeValue,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  IconButton,
  SimpleGrid,
  Box,
  Avatar,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Select,
  useToast,
  Tooltip,
  Spinner,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
} from '@chakra-ui/react'

// Types for vehicle data
interface Vehicle {
  id: string;
  registrationNumber: string;
  make?: string;
  model?: string;
  year?: number;
  totalSeats: number;
  status: 'active' | 'inactive' | 'maintenance';
  driver?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
  };
  conductor?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
  };
  currentRoute?: {
    id: string;
    name: string;
    startPoint: string;
    endPoint: string;
  };
  owner?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
  };
  createdAt: string;
  updatedAt: string;
  timetables?: Array<{
    id: string;
    dayOfWeek: number;
    departureTime: string;
    arrivalTime: string;
    active: boolean;
    route: {
      id: string;
      name: string;
      startPoint: string;
      endPoint: string;
    };
  }>;
}

// Stats will be updated with real data in the component

interface VehicleFormData {
  plateNumber: string
  model: string
  year: string
  capacity: number
  status: string
  route: string
  driver: string
  conductor: string
  owner: string
  layoutType: string
  layoutRows: number
  lastRowFull: boolean
  reservedSeats: number[]
  middleSeatRows: number[]
}

// ── Interactive bus seat editor ────────────────────────────────────────────
function BusSeatEditor({
  capacity,
  layoutType,
  layoutRows,
  lastRowFull,
  reservedSeats,
  onReservedSeatsChange,
  middleSeatRows,
  onMiddleSeatRowsChange,
}: {
  capacity: number
  layoutType: string
  layoutRows: number
  lastRowFull: boolean
  reservedSeats: number[]
  onReservedSeatsChange: (seats: number[]) => void
  middleSeatRows: number[]
  onMiddleSeatRowsChange: (rows: number[]) => void
}) {
  // Tanzania style: aisle after first seat from left for 2+1, after second for 2+2
  const leftCols = layoutType === '2+2' ? 2 : 1
  const rightCols = 2
  const baseCols = leftCols + rightCols

  // Calculate exactly how many rows we need to fit the capacity
  // 1 front seat + 4 back seats = 5 fixed seats.
  let rows = layoutRows
  if (rows === 0) {
    const middleNumberedNeeded = Math.max(capacity - 5, 0)
    // Front row is separate, so we only need middle rows + 1 for the mandatory back row.
    rows = Math.ceil(middleNumberedNeeded / baseCols) + 1
  }

  const hasFrontSingleSeat = capacity > 0
  const frontSeatNumber = 1
  const numberedStart = 2
  const numberedCount = capacity

  const SEAT_SZ = '30px'
  const MIDDLE_SZ = '30px'

  const toggleReserved = (n: number) =>
    onReservedSeatsChange(
      reservedSeats.includes(n) ? reservedSeats.filter((s) => s !== n) : [...reservedSeats, n]
    )

  const toggleMiddleRow = (rowNum: number) =>
    onMiddleSeatRowsChange(
      middleSeatRows.includes(rowNum)
        ? middleSeatRows.filter((r) => r !== rowNum)
        : [...middleSeatRows, rowNum].sort((a, b) => a - b)
    )

  // Build seat grid row-by-row in visual order (matches Flutter widget order)
  type GridCell = { n: number; kind: 'seat' } | { letter: string; kind: 'middle' } | { kind: 'empty' }
  const grid: GridCell[][] = []
  let numberedSeat = numberedStart
  let midLetter = 0

  for (let r = 0; r < rows; r++) {
    const rowNum = r + 1
    const isLast = r === rows - 1
    // Lettered middle seats are only for rows BEFORE the last row
    const hasMiddleToggle = middleSeatRows.includes(rowNum) && !isLast
    
    // Last row aisle filling:
    // 2+1 needs one seat in the aisle to reach 4 width.
    // 2+2 is already 4 width (2+2), so it doesn't need an extra aisle seat.
    const fillAisleWithSeat = isLast && layoutType === '2+1'
    
    const row: GridCell[] = []
    
    // Left Block
    for (let c = 0; c < leftCols; c++) {
      if (numberedSeat <= numberedCount) row.push({ n: numberedSeat++, kind: 'seat' })
      else row.push({ kind: 'empty' })
    }
    
    // Aisle Slot
    if (fillAisleWithSeat) {
      if (numberedSeat <= numberedCount) row.push({ n: numberedSeat++, kind: 'seat' })
      else row.push({ kind: 'empty' })
    } else if (hasMiddleToggle) {
      row.push({ letter: String.fromCharCode(65 + midLetter++), kind: 'middle' })
    } else {
      row.push({ kind: 'empty' })
    }

    // Right Block
    for (let c = 0; c < rightCols; c++) {
      if (numberedSeat <= numberedCount) row.push({ n: numberedSeat++, kind: 'seat' })
      else row.push({ kind: 'empty' })
    }
    grid.push(row)
  }

  const SeatCell = ({ cell }: { cell: GridCell }) => {
    if (cell.kind === 'empty') return <Box w={SEAT_SZ} h={SEAT_SZ} />

    if (cell.kind === 'middle') {
      return (
        <Box
          w={MIDDLE_SZ} h={MIDDLE_SZ}
          bg="white" border="2px solid" borderColor="gray.400"
          borderRadius="6px" display="flex" alignItems="center" justifyContent="center"
          title={`Middle/aisle seat ${cell.letter}`}
          flexShrink={0}
        >
          <Text fontSize="11px" fontWeight="bold" color="gray.700">{cell.letter}</Text>
        </Box>
      )
    }

    const isReserved = reservedSeats.includes(cell.n)
    return (
      <Box
        w={SEAT_SZ} h={SEAT_SZ}
        bg={isReserved ? 'red.500' : 'gray.700'}
        color="white"
        borderRadius="6px"
        display="flex" alignItems="center" justifyContent="center"
        cursor="pointer"
        onClick={() => toggleReserved(cell.n)}
        _hover={{ opacity: 0.75 }}
        title={isReserved ? `Seat ${cell.n}: reserved — click to restore` : `Seat ${cell.n}: available — click to mark reserved`}
        flexShrink={0}
      >
        <Text fontSize="10px" fontWeight="bold" lineHeight="1">{cell.n}</Text>
      </Box>
    )
  }

  const MiddleToggle = ({ rowNum, hasMiddle }: { rowNum: number; hasMiddle: boolean }) => (
    <Box
      w="22px" h={SEAT_SZ}
      border="1.5px dashed" borderColor={hasMiddle ? 'orange.400' : 'gray.300'}
      borderRadius="5px"
      display="flex" alignItems="center" justifyContent="center"
      cursor="pointer"
      bg={hasMiddle ? 'orange.50' : 'transparent'}
      onClick={() => toggleMiddleRow(rowNum)}
      _hover={{ bg: hasMiddle ? 'orange.100' : 'gray.100' }}
      title={hasMiddle ? 'Remove middle seat from this row' : 'Add middle seat to this row'}
      flexShrink={0}
    >
      <Text fontSize="12px" fontWeight="bold" color={hasMiddle ? 'orange.500' : 'gray.400'}>
        {hasMiddle ? '−' : '+'}
      </Text>
    </Box>
  )

  return (
    <Box>
      <Box
        bg="gray.200" borderRadius="24px"
        border="2px solid" borderColor="gray.300"
        p={4} display="inline-block" minW="240px"
        boxShadow="inner"
      >
        {/* Front row: Seat 1 | Gap | Driver */}
        <VStack align="stretch" spacing={1} mb={2}>
          <HStack spacing={2} justify="space-between" align="center">
            <Box
              w={SEAT_SZ} h={SEAT_SZ}
              bg={reservedSeats.includes(1) ? 'red.500' : 'gray.700'}
              color="white" borderRadius="6px"
              display="flex" alignItems="center" justifyContent="center"
              cursor="pointer" onClick={() => toggleReserved(1)}
              _hover={{ opacity: 0.75 }}
              title="Seat 1: Click to toggle reserved"
            >
              <Text fontSize="10px" fontWeight="bold">1</Text>
            </Box>
            
            <Box flex={1} />
            
            <Box 
              w={SEAT_SZ} h={SEAT_SZ} 
              bg="red.100" border="1.5px solid" borderColor="red.400" 
              borderRadius="6px" display="flex" alignItems="center" justifyContent="center"
              title="Driver Position"
            >
              <Text fontSize="10px" color="red.700" fontWeight="bold">D</Text>
            </Box>
          </HStack>
          <Box w="3px" h="15px" bg="gray.400" borderRadius="full" ml="13px" />
        </VStack>
        
        <Box h="1px" bg="gray.300" mb={3} />

        {/* Seat rows */}
        <VStack spacing={1} align="start">
          {grid.map((row, rIdx) => {
            const rowNum = rIdx + 1
            const hasMiddle = middleSeatRows.includes(rowNum)
            const isLast = rIdx === grid.length - 1
            
            // Last row is mandatory full (4 seats)
            // For 2+1 we show it as a solid block. For 2+2 we maintain the aisle gap.
            if (isLast && layoutType === '2+1') {
              return (
                <HStack key={rIdx} spacing={1} justify="center" w="full" pt={1}>
                  {row.map((cell, i) => <SeatCell key={i} cell={cell} />)}
                </HStack>
              )
            }

            const leftCells = row.filter((c) => c.kind !== 'middle').slice(0, leftCols)
            const midCell = row.find((c) => c.kind === 'middle')
            const rightCells = row.filter((c) => c.kind !== 'middle').slice(leftCols)

            return (
                <HStack key={rIdx} spacing={0} align="center" w="full" justify="center">
                  <HStack spacing={1}>{leftCells.map((cell, i) => <SeatCell key={i} cell={cell} />)}</HStack>
                  <Box w="40px" display="flex" justifyContent="center">
                    {midCell
                      ? <HStack spacing={0}><SeatCell cell={midCell} /><MiddleToggle rowNum={rowNum} hasMiddle={true} /></HStack>
                      : <MiddleToggle rowNum={rowNum} hasMiddle={false} />
                    }
                  </Box>
                  <HStack spacing={1}>{rightCells.map((cell, i) => <SeatCell key={i} cell={cell} />)}</HStack>
                </HStack>
            )
          })}
        </VStack>
      </Box>

      {/* Legend */}
      <HStack mt={2} spacing={3} flexWrap="wrap">
        <HStack spacing={1}><Box w="12px" h="12px" bg="gray.700" borderRadius="3px" /><Text fontSize="xs" color="gray.600">Available</Text></HStack>
        <HStack spacing={1}><Box w="12px" h="12px" bg="red.500" borderRadius="3px" /><Text fontSize="xs" color="gray.600">Reserved/Damaged</Text></HStack>
        <HStack spacing={1}><Box w="12px" h="12px" bg="white" border="1.5px solid" borderColor="gray.400" borderRadius="3px" /><Text fontSize="xs" color="gray.600">Middle seat</Text></HStack>
      </HStack>
      {reservedSeats.length > 0 && (
        <Text fontSize="xs" color="red.600" mt={1}>Reserved seats: {reservedSeats.sort((a, b) => a - b).join(', ')}</Text>
      )}
      {middleSeatRows.length > 0 && (
        <Text fontSize="xs" color="orange.600" mt={1}>Middle seat rows: {middleSeatRows.join(', ')}</Text>
      )}
      <Text fontSize="xs" color="gray.700" mt={1}>
        Bookable seats: {capacity - reservedSeats.length} (Total: {capacity})
      </Text>
      <Text fontSize="xs" color="gray.400" mt={1}>
        Click seat = toggle reserved · Click +/− = add/remove middle seat in that row
      </Text>
    </Box>
  )
}

// View Vehicle Modal
function VehicleViewModal({ isOpen, onClose, vehicle }: { isOpen: boolean; onClose: () => void; vehicle: Vehicle | null }) {
  if (!vehicle) return null

  const getDayName = (day: number) => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
    return days[day]
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={{ base: 'full', md: '2xl' }} scrollBehavior="inside">
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(10px)" />
      <ModalContent borderRadius={{ base: 'none', md: '2xl' }} m={{ base: 0, md: 4 }}>
        <ModalHeader>
          <HStack spacing={3}>
            <Box p={2} bg="blue.100" borderRadius="lg">
              <Truck size={20} color="var(--chakra-colors-blue-600)" />
            </Box>
            <VStack align="start" spacing={0}>
              <Text fontSize="xl" fontWeight="bold">{vehicle.registrationNumber}</Text>
              <Text fontSize="sm" color="gray.600">Vehicle Details</Text>
            </VStack>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <VStack spacing={6} align="stretch">
            <Card bg="gray.50" border="1px solid" borderColor="gray.200">
              <CardBody>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <VStack align="start" spacing={2}>
                    <Text fontSize="sm" fontWeight="medium" color="gray.600">Registration Number</Text>
                    <Text fontSize="lg" fontWeight="semibold" color="gray.900">{vehicle.registrationNumber}</Text>
                  </VStack>
                  <VStack align="start" spacing={2}>
                    <Text fontSize="sm" fontWeight="medium" color="gray.600">Status</Text>
                    <Badge
                      colorScheme={
                        vehicle.status === 'active' ? 'green' :
                          vehicle.status === 'inactive' ? 'red' : 'yellow'
                      }
                      variant="subtle"
                      borderRadius="full"
                      px={3}
                      py={1}
                      textTransform="capitalize"
                    >
                      {vehicle.status}
                    </Badge>
                  </VStack>
                </SimpleGrid>
              </CardBody>
            </Card>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <Card bg="gray.50" border="1px solid" borderColor="gray.200">
                <CardBody>
                  <VStack align="start" spacing={2}>
                    <Text fontSize="sm" fontWeight="medium" color="gray.600">Model</Text>
                    <Text fontSize="lg" fontWeight="semibold" color="gray.900">
                      {vehicle.model || 'N/A'} {vehicle.year && `(${vehicle.year})`}
                    </Text>
                  </VStack>
                </CardBody>
              </Card>
              <Card bg="gray.50" border="1px solid" borderColor="gray.200">
                <CardBody>
                  <VStack align="start" spacing={2}>
                    <Text fontSize="sm" fontWeight="medium" color="gray.600">Total Seats</Text>
                    <Text fontSize="lg" fontWeight="semibold" color="gray.900">
                      {vehicle.totalSeats - ((vehicle as any).seatLayout?.reservedSeats?.length || 0)} / {vehicle.totalSeats} seats
                    </Text>
                    {((vehicle as any).seatLayout?.reservedSeats?.length || 0) > 0 && (
                      <Text fontSize="xs" color="red.500">
                        ({(vehicle as any).seatLayout.reservedSeats.length} reserved/damaged)
                      </Text>
                    )}
                  </VStack>
                </CardBody>
              </Card>
            </SimpleGrid>

            {vehicle.driver && (
              <Card bg="gray.50" border="1px solid" borderColor="gray.200">
                <CardBody>
                  <VStack align="start" spacing={3}>
                    <HStack spacing={2}>
                      <User size={16} color="var(--chakra-colors-gray-600)" />
                      <Text fontSize="sm" fontWeight="medium" color="gray.600">Driver</Text>
                    </HStack>
                    <VStack align="start" spacing={1}>
                      <Text fontSize="md" fontWeight="semibold" color="gray.900">
                        {vehicle.driver.firstName} {vehicle.driver.lastName}
                      </Text>
                      <HStack spacing={4} flexWrap="wrap">
                        <HStack spacing={1}>
                          <Mail size={14} />
                          <Text fontSize="sm" color="gray.600">{vehicle.driver.email}</Text>
                        </HStack>
                        <HStack spacing={1}>
                          <Phone size={14} />
                          <Text fontSize="sm" color="gray.600">{vehicle.driver.phoneNumber}</Text>
                        </HStack>
                      </HStack>
                    </VStack>
                  </VStack>
                </CardBody>
              </Card>
            )}

            {vehicle.conductor && (
              <Card bg="gray.50" border="1px solid" borderColor="gray.200">
                <CardBody>
                  <VStack align="start" spacing={3}>
                    <HStack spacing={2}>
                      <User size={16} color="var(--chakra-colors-gray-600)" />
                      <Text fontSize="sm" fontWeight="medium" color="gray.600">Conductor</Text>
                    </HStack>
                    <VStack align="start" spacing={1}>
                      <Text fontSize="md" fontWeight="semibold" color="gray.900">
                        {vehicle.conductor.firstName} {vehicle.conductor.lastName}
                      </Text>
                      <HStack spacing={4} flexWrap="wrap">
                        <HStack spacing={1}>
                          <Mail size={14} />
                          <Text fontSize="sm" color="gray.600">{vehicle.conductor.email}</Text>
                        </HStack>
                        <HStack spacing={1}>
                          <Phone size={14} />
                          <Text fontSize="sm" color="gray.600">{vehicle.conductor.phoneNumber}</Text>
                        </HStack>
                      </HStack>
                    </VStack>
                  </VStack>
                </CardBody>
              </Card>
            )}

            {vehicle.currentRoute && (
              <Card bg="gray.50" border="1px solid" borderColor="gray.200">
                <CardBody>
                  <VStack align="start" spacing={2}>
                    <HStack spacing={2}>
                      <MapPin size={16} color="var(--chakra-colors-gray-600)" />
                      <Text fontSize="sm" fontWeight="medium" color="gray.600">Current Route</Text>
                    </HStack>
                    <Text fontSize="md" fontWeight="semibold" color="gray.900">{vehicle.currentRoute.name}</Text>
                    <Text fontSize="sm" color="gray.600">
                      {vehicle.currentRoute.startPoint} → {vehicle.currentRoute.endPoint}
                    </Text>
                  </VStack>
                </CardBody>
              </Card>
            )}

            {vehicle.timetables && vehicle.timetables.length > 0 && (
              <Card bg="gray.50" border="1px solid" borderColor="gray.200">
                <CardBody>
                  <VStack align="start" spacing={3}>
                    <Text fontSize="sm" fontWeight="medium" color="gray.600">Timetables ({vehicle.timetables.length})</Text>
                    <VStack spacing={2} align="stretch" w="full">
                      {vehicle.timetables.slice(0, 5).map((timetable) => (
                        <HStack key={timetable.id} p={3} bg="white" borderRadius="lg" justify="space-between">
                          <VStack align="start" spacing={0}>
                            <Text fontWeight="medium" color="gray.900">{getDayName(timetable.dayOfWeek)}</Text>
                            <Text fontSize="sm" color="gray.600">{timetable.route.name}</Text>
                          </VStack>
                          <VStack align="end" spacing={0}>
                            <Text fontSize="sm" color="gray.900">
                              {timetable.departureTime} - {timetable.arrivalTime}
                            </Text>
                            <Badge colorScheme={timetable.active ? 'green' : 'gray'} size="sm">
                              {timetable.active ? 'Active' : 'Inactive'}
                            </Badge>
                          </VStack>
                        </HStack>
                      ))}
                      {vehicle.timetables.length > 5 && (
                        <Text fontSize="sm" color="gray.500" textAlign="center">
                          +{vehicle.timetables.length - 5} more
                        </Text>
                      )}
                    </VStack>
                  </VStack>
                </CardBody>
              </Card>
            )}

            <HStack spacing={3} w="full" pt={4}>
              <Button variant="outline" onClick={onClose} flex={1} borderRadius="xl">
                Close
              </Button>
            </HStack>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

// Edit Vehicle Modal
function VehicleEditModal({ isOpen, onClose, vehicle }: { isOpen: boolean; onClose: () => void; vehicle: Vehicle | null }) {
  const [isLoading, setIsLoading] = useState(false)
  const toast = useToast()
  const queryClient = useQueryClient()
  const [formData, setFormData] = useState<VehicleFormData>({
    plateNumber: '',
    model: '',
    year: '',
    capacity: 16,
    status: 'active',
    route: '',
    driver: '',
    conductor: '',
    owner: '',
    layoutType: '2+1',
    layoutRows: 0,
    lastRowFull: true,
    reservedSeats: [] as number[],
    middleSeatRows: [] as number[],
  })

  const updateVehicleMutation = useMutation(
    (data: any) => cmsService.updateVehicle(vehicle?.id || '', data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('vehicles')
        queryClient.invalidateQueries('dashboard-stats')
        toast({
          title: 'Vehicle Updated',
          description: 'Vehicle details have been successfully saved.',
          status: 'success',
          duration: 3000,
          isClosable: true,
        })
        onClose()
      },
      onError: (error: any) => {
        toast({
          title: 'Update Failed',
          description: error?.response?.data?.error?.message || 'Failed to update vehicle. Please try again.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        })
      }
    }
  )


  // Fetch real routes for the dropdown
  const { data: routesData } = useQuery(
    'routes-for-vehicle-edit',
    () => cmsService.getRoutes({ limit: 100 }),
    { enabled: isOpen, retry: false }
  )

  // Fetch drivers (and conductors) for the dropdown
  const { data: driversData } = useQuery(
    'drivers-for-vehicle-edit',
    () => cmsService.getDrivers({ limit: 100 }),
    { enabled: isOpen, retry: false }
  )

  // Only Daladala Smart staff assign a bus to a company; companies always register under themselves.
  const isAdmin = useAuthStore(s => s.user?.role) === 'admin'
  const { data: operatorsData } = useQuery(
    'operators-for-vehicle-edit',
    () => cmsService.getOperators(),
    { enabled: isOpen && isAdmin, retry: false }
  )

  const routes = routesData?.data || []
  const drivers = driversData?.data || []
  const operators = operatorsData?.data || []

  React.useEffect(() => {
    if (vehicle) {
      const seatLayout = (vehicle as any).seatLayout as {
        layoutType?: string; rows?: number; lastRowFull?: boolean;
        reservedSeats?: number[]; middleSeatRows?: number[]
      } | undefined
      setFormData({
        plateNumber: vehicle.registrationNumber || '',
        model: vehicle.model || '',
        year: vehicle.year?.toString() || '',
        capacity: vehicle.totalSeats || 16,
        status: vehicle.status || 'active',
        route: vehicle.currentRoute?.id || '',
        driver: vehicle.driver?.id || '',
        conductor: vehicle.conductor?.id || '',
        owner: (vehicle as any).operatorId || '',
        layoutType: seatLayout?.layoutType || '2+1',
        layoutRows: seatLayout?.rows || 0,
        lastRowFull: seatLayout?.lastRowFull ?? true,
        reservedSeats: seatLayout?.reservedSeats ?? [],
        middleSeatRows: seatLayout?.middleSeatRows ?? []
      })
    }
  }, [vehicle])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)

    try {
      if (!vehicle) {
        toast({ title: 'Error', description: 'No vehicle selected', status: 'error' })
        return
      }

      await updateVehicleMutation.mutateAsync({
        registrationNumber: formData.plateNumber,
        make: formData.model.split(' ')[0],
        model: formData.model,
        year: formData.year ? parseInt(formData.year) : undefined,
        totalSeats: formData.capacity,
        seatLayout: {
          layoutType: formData.layoutType,
          rows: formData.layoutRows || undefined,
          lastRowFull: formData.lastRowFull,
          reservedSeats: formData.reservedSeats.length > 0 ? formData.reservedSeats : undefined,
          middleSeatRows: formData.middleSeatRows.length > 0 ? formData.middleSeatRows : undefined,
        },
        status: formData.status as 'active' | 'inactive' | 'maintenance',
        driverId: formData.driver || null,
        conductorId: formData.conductor || null,
        ...(isAdmin && formData.owner ? { operatorId: formData.owner } : {}),
        currentRouteId: formData.route || null
      })

      onClose()
    } catch (error: any) {
      let errorMessage = 'Failed to update vehicle.'

      if (error?.response?.data?.error) {
        const err = error.response.data.error
        errorMessage = typeof err === 'string' ? err : err.message || JSON.stringify(err)
      } else if (error?.message) {
        errorMessage = error.message
      }

      toast({
        title: 'Error',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    } finally {
      setIsLoading(false)
    }
  }

  if (!vehicle) return null

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={{ base: 'full', md: 'xl' }}>
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(10px)" />
      <ModalContent borderRadius={{ base: 'none', md: '2xl' }} m={{ base: 0, md: 4 }} maxH={{ base: '100vh', md: '90vh' }}>
        <ModalHeader>
          <HStack spacing={3}>
            <Box p={2} bg="blue.100" borderRadius="lg">
              <Edit size={20} color="var(--chakra-colors-blue-600)" />
            </Box>
            <Text fontSize="xl" fontWeight="bold">Edit Vehicle</Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6} overflowY="auto">
          <form onSubmit={handleSubmit}>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel fontSize="sm" fontWeight="medium">Registration Number</FormLabel>
                <Input
                  value={formData.plateNumber}
                  onChange={(e) => setFormData({ ...formData, plateNumber: e.target.value })}
                  placeholder="e.g., T 123 ABC"
                  borderRadius="xl"
                />
              </FormControl>

              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} w="full">
                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="medium">Model</FormLabel>
                  <Input
                    value={formData.model}
                    onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                    placeholder="e.g., Toyota Hiace"
                    borderRadius="xl"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="medium">Year</FormLabel>
                  <Input
                    type="number"
                    value={formData.year}
                    onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                    placeholder="e.g., 2020"
                    borderRadius="xl"
                  />
                </FormControl>
              </SimpleGrid>

              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} w="full">
                <FormControl isRequired>
                  <FormLabel fontSize="sm" fontWeight="medium">Capacity *</FormLabel>
                  <Input
                    type="number"
                    value={formData.capacity}
                    onChange={(e) => setFormData({ ...formData, capacity: parseInt(e.target.value) || 16 })}
                    min={1}
                    max={50}
                    borderRadius="xl"
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel fontSize="sm" fontWeight="medium">Status</FormLabel>
                  <Select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    borderRadius="xl"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="maintenance">Maintenance</option>
                  </Select>
                </FormControl>
                
                {isAdmin && (
                  <FormControl>
                  <FormLabel fontSize="sm" fontWeight="medium">Company</FormLabel>
                  <Select
                    value={formData.owner}
                    onChange={(e) => setFormData({ ...formData, owner: e.target.value })}
                    placeholder="Daladala Smart fleet"
                    borderRadius="xl"
                  >
                    {operators.map((operator: any) => (
                      <option key={operator.id} value={operator.id}>
                        {operator.firstName} {operator.lastName}
                      </option>
                    ))}
                  </Select>
                </FormControl>
                  )}
              </SimpleGrid>

              <Box w="full" p={3} bg="gray.50" borderRadius="lg" borderWidth="1px" borderColor="gray.200">
                <Text fontSize="sm" fontWeight="semibold" mb={2} color="gray.700">Seat layout (for passenger app)</Text>
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} w="full">
                  <FormControl>
                    <FormLabel fontSize="xs">Layout</FormLabel>
                    <Select
                      value={formData.layoutType}
                      onChange={(e) => setFormData({ ...formData, layoutType: e.target.value })}
                      borderRadius="xl"
                      size="sm"
                    >
                      <option value="2+1">2+1 (Tanzania style)</option>
                      <option value="2+2">2+2</option>
                    </Select>
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="xs">Rows (0 = auto)</FormLabel>
                    <Input
                      type="number"
                      value={formData.layoutRows || ''}
                      onChange={(e) => setFormData({ ...formData, layoutRows: parseInt(e.target.value) || 0 })}
                      placeholder="Auto"
                      borderRadius="xl"
                      size="sm"
                      min={0}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="xs" whiteSpace="nowrap">Last row full</FormLabel>
                    <Flex align="center" h="32px">
                      <input
                        type="checkbox"
                        checked={formData.lastRowFull}
                        onChange={(e) => setFormData({ ...formData, lastRowFull: e.target.checked })}
                      />
                      <Text ml={2} fontSize="sm">Yes</Text>
                    </Flex>
                  </FormControl>
                </SimpleGrid>

                {/* Seat editor: reserved / middle seats */}
                <Box w="full" pt={2}>
                  <Text fontSize="xs" fontWeight="semibold" color="gray.700" mb={2}>
                    Seat map — click seat to reserve/restore · click +/− to add/remove middle seat in row
                  </Text>
                  <BusSeatEditor
                    capacity={formData.capacity}
                    layoutType={formData.layoutType}
                    layoutRows={formData.layoutRows}
                    lastRowFull={formData.lastRowFull}
                    reservedSeats={formData.reservedSeats}
                    onReservedSeatsChange={(seats) => setFormData({ ...formData, reservedSeats: seats })}
                    middleSeatRows={formData.middleSeatRows}
                    onMiddleSeatRowsChange={(rows) => setFormData({ ...formData, middleSeatRows: rows })}
                  />
                </Box>
              </Box>

              <FormControl>
                <FormLabel fontSize="sm" fontWeight="medium">Assigned Route</FormLabel>
                <Select
                  value={formData.route}
                  onChange={(e) => setFormData({ ...formData, route: e.target.value })}
                  borderRadius="xl"
                  placeholder="Select a route"
                >
                  <option value="">Select Route</option>
                  {routes.map((route: any) => (
                    <option key={route.id} value={route.id}>
                      {route.name} ({route.startPoint} - {route.endPoint})
                    </option>
                  ))}
                </Select>
              </FormControl>

              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} w="full">
                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="medium">Assigned Driver</FormLabel>
                  <Select
                    value={formData.driver}
                    onChange={(e) => setFormData({ ...formData, driver: e.target.value })}
                    borderRadius="xl"
                    placeholder="Select a driver"
                  >
                    <option value="">Select Driver</option>
                    {drivers.map((driver: any) => (
                      <option key={driver.id} value={driver.id}>
                        {driver.firstName} {driver.lastName} ({driver.phoneNumber})
                      </option>
                    ))}
                  </Select>
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="medium">Assigned Conductor</FormLabel>
                  <Select
                    value={formData.conductor}
                    onChange={(e) => setFormData({ ...formData, conductor: e.target.value })}
                    borderRadius="xl"
                    placeholder="Select a conductor"
                  >
                    <option value="">Select Conductor</option>
                    {drivers.map((driver: any) => (
                      <option key={driver.id} value={driver.id}>
                        {driver.firstName} {driver.lastName} ({driver.phoneNumber})
                      </option>
                    ))}
                  </Select>
                </FormControl>
              </SimpleGrid>

              <HStack spacing={3} w="full" pt={4}>
                <Button
                  type="button"
                  variant="outline"
                  onClick={onClose}
                  flex={1}
                  borderRadius="xl"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  colorScheme="brand"
                  flex={1}
                  borderRadius="xl"
                  isLoading={isLoading}
                  loadingText="Updating..."
                  leftIcon={<Edit size={16} />}
                >
                  Update Vehicle
                </Button>
              </HStack>
            </VStack>
          </form>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

// Vehicle Delete Confirmation Dialog
function VehicleDeleteDialog({ isOpen, onClose, onConfirm, vehicleId }: { 
  isOpen: boolean; 
  onClose: () => void; 
  onConfirm: () => void;
  vehicleId: string | null;
}) {
  const cancelRef = React.useRef<HTMLButtonElement>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  const handleDelete = async () => {
    setIsDeleting(true)
    await onConfirm()
    setIsDeleting(false)
  }

  return (
    <AlertDialog
      isOpen={isOpen}
      leastDestructiveRef={cancelRef}
      onClose={onClose}
    >
      <AlertDialogOverlay>
        <AlertDialogContent borderRadius="xl">
          <AlertDialogHeader fontSize="lg" fontWeight="bold">
            Delete Vehicle
          </AlertDialogHeader>

          <AlertDialogBody>
            Are you sure you want to delete this vehicle? This action cannot be undone and will remove all associated data.
          </AlertDialogBody>

          <AlertDialogFooter>
            <Button ref={cancelRef} onClick={onClose} borderRadius="xl">
              Cancel
            </Button>
            <Button 
              colorScheme="red" 
              onClick={handleDelete} 
              ml={3} 
              isLoading={isDeleting} 
              loadingText="Deleting..."
              borderRadius="xl"
            >
              Delete
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  )
}

function StatCard({ title, value, color, icon: Icon }: any) {
  const colorMap = {
    blue: { bg: "blue.100", color: "blue.600", iconBg: "blue.500" },
    green: { bg: "green.100", color: "green.600", iconBg: "green.500" },
    orange: { bg: "orange.100", color: "orange.600", iconBg: "orange.500" },
    red: { bg: "red.100", color: "red.600", iconBg: "red.500" },
    gray: { bg: "gray.100", color: "gray.600", iconBg: "gray.500" }
  }

  const colors = colorMap[color as keyof typeof colorMap] || colorMap.blue

  return (
    <Card variant="glass" p={4}>
      <HStack spacing={4}>
        <Box p={3} bg={colors.iconBg} borderRadius="xl">
          <Icon size={24} color="white" />
        </Box>
        <VStack align="start" spacing={0}>
          <Text fontSize="2xl" fontWeight="bold" color="gray.900">
            {value}
          </Text>
          <Text fontSize="sm" color="gray.600">
            {title}
          </Text>
        </VStack>
      </HStack>
    </Card>
  )
}

function VehicleFormModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const [formData, setFormData] = useState<VehicleFormData>({
    plateNumber: '',
    model: '',
    year: '',
    capacity: 16,
    status: 'active',
    route: '',
    driver: '',
    conductor: '',
    owner: '',
    layoutType: '2+1',
    layoutRows: 0,
    lastRowFull: true,
    reservedSeats: [],
    middleSeatRows: []
  })
  const [isLoading, setIsLoading] = useState(false)
  const toast = useToast()

  const queryClient = useQueryClient()

  // Fetch real routes for the dropdown
  const { data: routesData } = useQuery(
    'routes-for-vehicles',
    () => cmsService.getRoutes({ limit: 100 }), // Get all routes
    {
      enabled: isOpen, // Only fetch when modal is open
      retry: false,
    }
  )

  // Fetch drivers for the dropdown
  const { data: driversData } = useQuery(
    'drivers-for-vehicles',
    () => cmsService.getDrivers({ limit: 100 }), // Get all drivers
    {
      enabled: isOpen, // Only fetch when modal is open
      retry: false,
    }
  )

  // Only Daladala Smart staff assign a bus to a company; companies always register under themselves.
  const isAdmin = useAuthStore(s => s.user?.role) === 'admin'
  const { data: operatorsData } = useQuery(
    'operators-for-vehicles',
    () => cmsService.getOperators(),
    {
      enabled: isOpen && isAdmin,
      retry: false,
    }
  )

  const routes = routesData?.data || []
  const drivers = driversData?.data || []
  const operators = operatorsData?.data || []

  const createVehicleMutation = useMutation(
    (vehicleData: any) => cmsService.createVehicle(vehicleData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('vehicles')
        toast({
          title: 'Vehicle Added',
          description: `${formData.model} (${formData.plateNumber}) has been successfully added to the fleet.`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        })
        onClose()
        setFormData({
          plateNumber: '',
          model: '',
          year: '',
          capacity: 16,
          status: 'active',
          route: '',
          driver: '',
          conductor: '',
          owner: '',
          layoutType: '2+1',
          layoutRows: 0,
          lastRowFull: true,
          reservedSeats: [],
          middleSeatRows: []
        })
      },
      onError: (error: any) => {
        let errorMessage = 'Failed to add vehicle. Please try again.'

        if (error?.response?.data?.error) {
          const err = error.response.data.error
          errorMessage = typeof err === 'string' ? err : err.message || JSON.stringify(err)
        } else if (error?.message) {
          errorMessage = error.message
        }

        toast({
          title: 'Error',
          description: errorMessage,
          status: 'error',
          duration: 5000,
          isClosable: true,
        })
      }
    }
  )

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Validate required fields
    if (!formData.plateNumber || !formData.plateNumber.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Plate number is required',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      })
      return
    }

    if (!formData.capacity || formData.capacity < 1) {
      toast({
        title: 'Validation Error',
        description: 'Capacity must be at least 1',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      })
      return
    }

    setIsLoading(true)

    try {
      // Clean up data before sending
      const vehicleData: any = {
        registrationNumber: formData.plateNumber.trim(),
        totalSeats: formData.capacity,
      }

      // Add optional fields only if they have values
      if (formData.model && formData.model.trim()) {
        vehicleData.model = formData.model.trim()
        vehicleData.make = formData.model.split(' ')[0] // First word as make
      }

      if (formData.year && formData.year.trim()) {
        vehicleData.year = parseInt(formData.year)
      }

      if (formData.route) {
        vehicleData.currentRouteId = formData.route
      }

      if (formData.driver) {
        vehicleData.driverId = formData.driver
      }

      if (formData.conductor) {
        vehicleData.conductorId = formData.conductor
      }

      if (isAdmin && formData.owner) {
        vehicleData.operatorId = formData.owner
      }

      vehicleData.seatLayout = {
        layoutType: formData.layoutType,
        rows: formData.layoutRows || undefined,
        lastRowFull: formData.lastRowFull,
        reservedSeats: formData.reservedSeats.length > 0 ? formData.reservedSeats : undefined,
        middleSeatRows: formData.middleSeatRows.length > 0 ? formData.middleSeatRows : undefined,
      }

      await createVehicleMutation.mutateAsync(vehicleData)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl">
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(10px)" />
      <ModalContent borderRadius="2xl">
        <ModalHeader>
          <HStack spacing={3}>
            <Box p={2} bg="brand.100" borderRadius="lg">
              <Truck size={20} color="var(--chakra-colors-brand-600)" />
            </Box>
            <Text fontSize="xl" fontWeight="bold">Add New Vehicle</Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <form onSubmit={handleSubmit}>
            <VStack spacing={6} align="stretch">
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                <FormControl isRequired>
                  <FormLabel fontSize="sm" fontWeight="medium">Plate Number *</FormLabel>
                  <Input
                    value={formData.plateNumber}
                    onChange={(e) => setFormData({ ...formData, plateNumber: e.target.value })}
                    placeholder="Enter plate number"
                    borderRadius="xl"
                    borderColor="gray.300"
                    _focus={{ borderColor: 'brand.500', boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)' }}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="medium">Model</FormLabel>
                  <Input
                    value={formData.model}
                    onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                    placeholder="Enter vehicle model (optional)"
                    borderRadius="xl"
                    borderColor="gray.300"
                    _focus={{ borderColor: 'brand.500', boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)' }}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="medium">Year</FormLabel>
                  <Input
                    type="number"
                    value={formData.year}
                    onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                    placeholder="Enter year (optional)"
                    borderRadius="xl"
                    borderColor="gray.300"
                    _focus={{ borderColor: 'brand.500', boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)' }}
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel fontSize="sm" fontWeight="medium">Capacity *</FormLabel>
                  <Input
                    type="number"
                    value={formData.capacity}
                    onChange={(e) => setFormData({ ...formData, capacity: parseInt(e.target.value) })}
                    placeholder="Enter passenger capacity"
                    borderRadius="xl"
                    borderColor="gray.300"
                    _focus={{ borderColor: 'brand.500', boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)' }}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="medium">Status</FormLabel>
                  <Select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    borderRadius="xl"
                    borderColor="gray.300"
                    _focus={{ borderColor: 'brand.500', boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)' }}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="maintenance">Maintenance</option>
                  </Select>
                </FormControl>

                {isAdmin && (
                  <FormControl>
                  <FormLabel fontSize="sm" fontWeight="medium">Company</FormLabel>
                  <Select
                    value={formData.owner}
                    onChange={(e) => setFormData({ ...formData, owner: e.target.value })}
                    placeholder="Daladala Smart fleet"
                    borderRadius="xl"
                    borderColor="gray.300"
                    _focus={{ borderColor: 'brand.500', boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)' }}
                  >
                    {operators.map((operator: any) => (
                      <option key={operator.id} value={operator.id}>
                        {operator.firstName} {operator.lastName}
                      </option>
                    ))}
                  </Select>
                </FormControl>
                  )}

                <Box gridColumn="1 / -1" p={3} bg="gray.50" borderRadius="xl" borderWidth="1px" borderColor="gray.200">
                  <Text fontSize="sm" fontWeight="semibold" mb={2} color="gray.700">Seat layout (for passenger app)</Text>
                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} w="full">
                    <FormControl>
                      <FormLabel fontSize="xs">Layout</FormLabel>
                      <Select
                        value={formData.layoutType}
                        onChange={(e) => setFormData({ ...formData, layoutType: e.target.value })}
                        borderRadius="xl"
                        size="sm"
                        borderColor="gray.300"
                      >
                        <option value="2+1">2+1 (Tanzania style)</option>
                        <option value="2+2">2+2</option>
                      </Select>
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="xs">Rows (0 = auto)</FormLabel>
                      <Input
                        type="number"
                        value={formData.layoutRows || ''}
                        onChange={(e) => setFormData({ ...formData, layoutRows: parseInt(e.target.value) || 0 })}
                        placeholder="Auto"
                        borderRadius="xl"
                        size="sm"
                        min={0}
                        borderColor="gray.300"
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="xs" whiteSpace="nowrap">Last row full</FormLabel>
                      <Flex align="center" h="32px">
                        <input
                          type="checkbox"
                          checked={formData.lastRowFull}
                          onChange={(e) => setFormData({ ...formData, lastRowFull: e.target.checked })}
                        />
                        <Text ml={2} fontSize="sm">Yes</Text>
                      </Flex>
                    </FormControl>
                  </SimpleGrid>

                  {/* Seat editor: reserved / middle seats */}
                  <Box w="full" pt={2}>
                    <Text fontSize="xs" fontWeight="semibold" color="gray.700" mb={2}>
                      Seat map — click seat to reserve/restore · click +/− to add/remove middle seat in row
                    </Text>
                    <BusSeatEditor
                      capacity={formData.capacity}
                      layoutType={formData.layoutType}
                      layoutRows={formData.layoutRows}
                      lastRowFull={formData.lastRowFull}
                      reservedSeats={formData.reservedSeats}
                      onReservedSeatsChange={(seats) => setFormData({ ...formData, reservedSeats: seats })}
                      middleSeatRows={formData.middleSeatRows}
                      onMiddleSeatRowsChange={(rows) => setFormData({ ...formData, middleSeatRows: rows })}
                    />
                  </Box>
                </Box>

                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="medium">Assigned Route</FormLabel>
                  <Select
                    value={formData.route}
                    onChange={(e) => setFormData({ ...formData, route: e.target.value })}
                    borderRadius="xl"
                    borderColor="gray.300"
                    _focus={{ borderColor: 'brand.500', boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)' }}
                    placeholder="Select a route"
                  >
                    <option value="">Select Route</option>
                    {routes.map((route: any) => (
                      <option key={route.id} value={route.id}>
                        {route.name} ({route.startPoint} - {route.endPoint})
                      </option>
                    ))}
                  </Select>
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="medium">Assigned Driver</FormLabel>
                  <Select
                    value={formData.driver}
                    onChange={(e) => setFormData({ ...formData, driver: e.target.value })}
                    borderRadius="xl"
                    borderColor="gray.300"
                    _focus={{ borderColor: 'brand.500', boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)' }}
                    placeholder="Select a driver"
                  >
                    <option value="">Select Driver</option>
                    {drivers.map((driver: any) => (
                      <option key={driver.id} value={driver.id}>
                        {driver.firstName} {driver.lastName} ({driver.phoneNumber})
                      </option>
                    ))}
                  </Select>
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="medium">Assigned Conductor</FormLabel>
                  <Select
                    value={formData.conductor}
                    onChange={(e) => setFormData({ ...formData, conductor: e.target.value })}
                    borderRadius="xl"
                    borderColor="gray.300"
                    _focus={{ borderColor: 'brand.500', boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)' }}
                    placeholder="Select a conductor"
                  >
                    <option value="">Select Conductor</option>
                    {drivers.map((driver: any) => (
                      <option key={driver.id} value={driver.id}>
                        {driver.firstName} {driver.lastName} ({driver.phoneNumber})
                      </option>
                    ))}
                  </Select>
                </FormControl>
              </SimpleGrid>

              <HStack spacing={3} pt={4}>
                <Button
                  type="submit"
                  colorScheme="brand"
                  isLoading={isLoading}
                  loadingText="Adding..."
                  leftIcon={<Plus size={16} />}
                  borderRadius="xl"
                  flex={1}
                >
                  Add Vehicle
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={onClose}
                  borderRadius="xl"
                  borderColor="gray.300"
                  _hover={{ bg: 'gray.50' }}
                  flex={1}
                >
                  Cancel
                </Button>
              </HStack>
            </VStack>
          </form>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

export default function VehiclesPage() {
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedStatus, setSelectedStatus] = useState('all')
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(10)
  const { isOpen, onOpen, onClose } = useDisclosure()
  const { isOpen: isViewOpen, onOpen: onViewOpen, onClose: onViewClose } = useDisclosure()
  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure()
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null)
  const toast = useToast()
  const queryClient = useQueryClient()

  // Fetch real data from CMS
  const { data: vehiclesData, isLoading: vehiclesLoading } = useQuery(
    'vehicles',
    () => cmsService.getVehicles({ limit: 100 }),
    {
      retry: false,
    }
  )

  const { data: dashboardStats } = useQuery(
    'dashboard-stats',
    () => cmsService.getDashboardStats(),
    {
      retry: false,
    }
  )

  useEffect(() => {
    setCurrentPage(1)
  }, [searchTerm, selectedStatus])

  // Use real data only - no fallbacks
  const vehicles: Vehicle[] = vehiclesData?.data || []
  const stats = [
    { title: "Total Vehicles", value: dashboardStats?.data?.totalDaladalas || 0, color: "blue", icon: Truck },
    { title: "Active", value: dashboardStats?.data?.activeDaladalas || 0, color: "green", icon: CheckCircle },
    { title: "Maintenance", value: dashboardStats?.data?.maintenanceDaladalas || 0, color: "orange", icon: Wrench },
    { title: "Inactive", value: dashboardStats?.data?.inactiveDaladalas || 0, color: "red", icon: XCircle },
    { title: "With Drivers", value: dashboardStats?.data?.daladalasWithDrivers || 0, color: "blue", icon: User },
    { title: "Available Drivers", value: dashboardStats?.data?.availableDrivers || 0, color: "orange", icon: Fuel }
  ]

  const filteredVehicles = vehicles.filter((vehicle: Vehicle) => {
    const registrationNumber = vehicle.registrationNumber || ''
    const model = vehicle.model || ''
    const driverName = vehicle.driver?.firstName ? `${vehicle.driver.firstName} ${vehicle.driver.lastName}` : ''

    const matchesSearch = registrationNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      model.toLowerCase().includes(searchTerm.toLowerCase()) ||
      driverName.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesStatus = selectedStatus === 'all' || vehicle.status?.toLowerCase() === selectedStatus.toLowerCase()
    return matchesSearch && matchesStatus
  })

  // Pagination logic
  const totalPages = Math.ceil(filteredVehicles.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const currentVehicles = filteredVehicles.slice(startIndex, endIndex)

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const handleExport = () => {
    toast({
      title: 'Export Started',
      description: 'Your vehicles data is being exported.',
      status: 'success',
      duration: 3000,
      isClosable: true,
    })
  }

  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure()
  const [vehicleToDelete, setVehicleToDelete] = useState<string | null>(null)

  const handleViewVehicle = (vehicle: Vehicle) => {
    setSelectedVehicle(vehicle)
    onViewOpen()
  }

  const handleEditVehicle = (vehicle: Vehicle) => {
    setSelectedVehicle(vehicle)
    onEditOpen()
  }

  const handleDeleteVehicle = (vehicleId: string) => {
    setVehicleToDelete(vehicleId)
    onDeleteOpen()
  }

  const confirmDeleteVehicle = async () => {
    if (!vehicleToDelete) return

    try {
      await cmsService.deleteVehicle(vehicleToDelete)
      queryClient.invalidateQueries('vehicles')
      
      toast({
        title: 'Vehicle Deleted',
        description: 'Vehicle has been successfully deleted.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      })
      
      onDeleteClose()
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error?.response?.data?.error?.message || 'Failed to delete vehicle.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    }
  }

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active': return 'green'
      case 'inactive': return 'gray'
      case 'maintenance': return 'orange'
      default: return 'gray'
    }
  }

  // Show loading state
  if (vehiclesLoading) {
    return (
      <Center h="64">
        <Spinner size="lg" color="brand.500" />
      </Center>
    )
  }

  return (
    <VStack spacing={6} align="stretch">
      {/* Header */}
      <Flex align="center" justify="space-between" flexDirection={{ base: 'column', md: 'row' }} gap={4}>
        <VStack align={{ base: 'center', md: 'start' }} spacing={1} textAlign={{ base: 'center', md: 'left' }}>
          <Heading size={{ base: 'md', md: 'lg' }} color="gray.900">
            Vehicles Management
          </Heading>
          <Text color="gray.600" fontSize={{ base: 'sm', md: 'md' }}>
            Manage fleet vehicles and maintenance records
          </Text>
        </VStack>
        <HStack spacing={3} w={{ base: 'full', md: 'auto' }} justify={{ base: 'center', md: 'flex-end' }}>
          <Button
            leftIcon={<Wrench size={16} />}
            colorScheme="orange"
            variant="outline"
            borderRadius="xl"
            borderColor="orange.300"
            color="orange.600"
            _hover={{ bg: 'orange.50' }}
            size={{ base: 'sm', md: 'md' }}
            display={{ base: 'none', sm: 'flex' }}
          >
            Maintenance
          </Button>
          <Button
            leftIcon={<Plus size={16} />}
            colorScheme="brand"
            borderRadius="xl"
            onClick={onOpen}
            size={{ base: 'sm', md: 'md' }}
            w={{ base: 'full', md: 'auto' }}
          >
            Add Vehicle
          </Button>
        </HStack>
      </Flex>

      {/* Stats Cards */}
      <SimpleGrid columns={{ base: 2, md: 3, lg: 6 }} spacing={4} minChildWidth="200px">
        {stats.map((stat, index) => (
          <StatCard key={index} {...stat} />
        ))}
      </SimpleGrid>

      {/* Search and Filter Section */}
      <Card variant="glass">
        <CardBody>
          <VStack spacing={4} align="stretch">
            <HStack spacing={4}>
              <InputGroup flex="1">
                <InputLeftElement>
                  <Search size={16} color="var(--chakra-colors-gray-400)" />
                </InputLeftElement>
                <Input
                  placeholder="Plate number, model, or driver name"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  borderRadius="xl"
                  borderColor="gray.300"
                  _focus={{
                    borderColor: 'brand.500',
                    boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)',
                  }}
                />
              </InputGroup>

              <Select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                w="200px"
                borderRadius="xl"
                borderColor="gray.300"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="maintenance">Maintenance</option>
              </Select>
            </HStack>

            <HStack spacing={3}>
              <Button
                colorScheme="brand"
                borderRadius="xl"
                onClick={() => {
                  setSearchTerm('');
                  setSelectedStatus('all');
                }}
              >
                Clear
              </Button>
            </HStack>
          </VStack>
        </CardBody>
      </Card>

      {/* Vehicles Table */}
      <Card variant="glass">
        <CardHeader>
          <Flex align="center" justify="space-between">
            <Heading size="md" color="gray.900">
              Vehicles ({filteredVehicles.length})
            </Heading>
            <Button
              leftIcon={<Download size={16} />}
              colorScheme="green"
              variant="outline"
              borderRadius="xl"
              onClick={handleExport}
            >
              Export
            </Button>
          </Flex>
        </CardHeader>
        <CardBody pt={0}>
          <Box overflowX="auto">
            <Table variant="simple" size="sm">
              <Thead>
                <Tr>
                  <Th borderBottom="2px solid" borderColor="gray.200" py={4}>Vehicle</Th>
                  <Th borderBottom="2px solid" borderColor="gray.200" py={4}>Driver</Th>
                  <Th borderBottom="2px solid" borderColor="gray.200" py={4}>Status</Th>
                  <Th borderBottom="2px solid" borderColor="gray.200" py={4}>Route</Th>
                  <Th borderBottom="2px solid" borderColor="gray.200" py={4}>Owner</Th>
                  <Th borderBottom="2px solid" borderColor="gray.200" py={4}>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {filteredVehicles.length === 0 ? (
                  <Tr>
                    <Td colSpan={8} py={12}>
                      <Center>
                        <VStack spacing={4}>
                          <Box p={4} bg="gray.100" borderRadius="full">
                            <Truck size={48} color="var(--chakra-colors-gray-500)" />
                          </Box>
                          <VStack spacing={2}>
                            <Heading size="md" color="gray.900">
                              No vehicles found
                            </Heading>
                            <Text color="gray.600" textAlign="center">
                              {searchTerm || selectedStatus !== 'all'
                                ? 'No vehicles match your current filters.'
                                : 'Get started by adding your first vehicle to the fleet.'
                              }
                            </Text>
                          </VStack>
                          {!searchTerm && selectedStatus === 'all' && (
                            <Button
                              colorScheme="brand"
                              leftIcon={<Plus size={16} />}
                              onClick={onOpen}
                              borderRadius="xl"
                            >
                              Add Your First Vehicle
                            </Button>
                          )}
                        </VStack>
                      </Center>
                    </Td>
                  </Tr>
                ) : (
                  currentVehicles.map((vehicle: Vehicle) => (
                    <Tr key={vehicle.id} _hover={{ bg: 'gray.50' }} borderBottom="1px solid" borderColor="gray.100">
                      <Td py={4}>
                        <HStack spacing={3}>
                          <Avatar size="sm" bg="blue.100" color="blue.600" icon={<Truck size={16} />} />
                          <VStack align="start" spacing={0}>
                            <Text fontWeight="medium" color="gray.900">
                              {vehicle.registrationNumber}
                            </Text>
                            <Text fontSize="sm" color="gray.600">
                              {vehicle.model} {vehicle.year && `(${vehicle.year})`}
                            </Text>
                            <Text fontSize="xs" color="gray.500">
                              Capacity: {vehicle.totalSeats - ((vehicle as any).seatLayout?.reservedSeats?.length || 0)} / {vehicle.totalSeats} seats
                            </Text>
                          </VStack>
                        </HStack>
                      </Td>
                      <Td py={4}>
                        {vehicle.driver ? (
                          <VStack align="start" spacing={1}>
                            <HStack spacing={2}>
                              <User size={14} color="var(--chakra-colors-gray-500)" />
                              <Text fontSize="sm" color="gray.900" fontWeight="medium">
                                {vehicle.driver.firstName} {vehicle.driver.lastName}
                              </Text>
                            </HStack>
                            <HStack spacing={2}>
                              <Phone size={12} color="var(--chakra-colors-gray-400)" />
                              <Text fontSize="xs" color="gray.600">
                                {vehicle.driver.phoneNumber}
                              </Text>
                            </HStack>
                          </VStack>
                        ) : (
                          <Text fontSize="sm" color="gray.500">
                            No driver assigned
                          </Text>
                        )}
                      </Td>
                      <Td py={4}>
                        <VStack align="start" spacing={1}>
                        <Badge
                          colorScheme={getStatusColor(vehicle.status)}
                          variant="subtle"
                          borderRadius="full"
                          fontSize="xs"
                        >
                          {vehicle.status}
                        </Badge>
                        {(vehicle as any).approvalStatus && (vehicle as any).approvalStatus !== 'approved' && (
                          <Tooltip hasArrow label={(vehicle as any).approvalNotes || 'Daladala Smart is checking this bus before it can run trips'}>
                            <Badge colorScheme={(vehicle as any).approvalStatus === 'rejected' ? 'red' : 'orange'} variant="subtle" borderRadius="full" fontSize="xs">
                              {(vehicle as any).approvalStatus === 'rejected' ? 'Rejected' : 'Awaiting approval'}
                            </Badge>
                          </Tooltip>
                        )}
                        </VStack>
                      </Td>
                      <Td py={4}>
                        <VStack align="start" spacing={1}>
                          <HStack spacing={2}>
                            <MapPin size={12} color="var(--chakra-colors-gray-500)" />
                            <Text fontSize="sm" color="gray.900">
                              {vehicle.currentRoute?.name || 'Not Assigned'}
                            </Text>
                          </HStack>
                          <Text fontSize="xs" color="gray.500">
                            Created: {new Date(vehicle.createdAt).toLocaleDateString()}
                          </Text>
                        </VStack>
                      </Td>
                      <Td py={4}>
                        {vehicle.owner ? (
                          <VStack align="start" spacing={0}>
                            <Text fontSize="sm" fontWeight="medium" color="gray.900">
                              {vehicle.owner.firstName} {vehicle.owner.lastName}
                            </Text>
                            <Text fontSize="xs" color="gray.500">
                              {vehicle.owner.email}
                            </Text>
                          </VStack>
                        ) : (
                          <Text fontSize="sm" color="gray.400">System</Text>
                        )}
                      </Td>
                      <Td py={4}>
                        <HStack spacing={1}>
                          <Tooltip label="View Details">
                            <IconButton
                              aria-label="View"
                              icon={<Eye size={16} />}
                              size="sm"
                              variant="ghost"
                              colorScheme="blue"
                              onClick={() => handleViewVehicle(vehicle)}
                            />
                          </Tooltip>
                          <Tooltip label="Edit Vehicle">
                            <IconButton
                              aria-label="Edit"
                              icon={<Edit size={16} />}
                              size="sm"
                              variant="ghost"
                              colorScheme="orange"
                              onClick={() => handleEditVehicle(vehicle)}
                            />
                          </Tooltip>
                          <Tooltip label="Delete Vehicle">
                            <IconButton
                              aria-label="Delete"
                              icon={<Trash2 size={16} />}
                              size="sm"
                              variant="ghost"
                              colorScheme="red"
                              onClick={() => handleDeleteVehicle(vehicle.id)}
                            />
                          </Tooltip>
                        </HStack>
                      </Td>
                    </Tr>
                  ))
                )}
              </Tbody>
            </Table>
          </Box>
        </CardBody>

        {/* Pagination */}
        {totalPages > 1 && (
          <CardBody pt={0}>
            <Flex justify="space-between" align="center">
              <Text fontSize="sm" color="gray.600">
                Showing {startIndex + 1} to {Math.min(endIndex, filteredVehicles.length)} of {filteredVehicles.length} vehicles
              </Text>
              <HStack spacing={2}>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handlePageChange(currentPage - 1)}
                  isDisabled={currentPage === 1}
                  borderRadius="xl"
                >
                  Previous
                </Button>

                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                  <Button
                    key={page}
                    size="sm"
                    variant={currentPage === page ? "solid" : "outline"}
                    colorScheme={currentPage === page ? "brand" : "gray"}
                    onClick={() => handlePageChange(page)}
                    borderRadius="xl"
                  >
                    {page}
                  </Button>
                ))}

                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handlePageChange(currentPage + 1)}
                  isDisabled={currentPage === totalPages}
                  borderRadius="xl"
                >
                  Next
                </Button>
              </HStack>
            </Flex>
          </CardBody>
        )}
      </Card>

      {/* Vehicle Form Modal */}
      <VehicleFormModal isOpen={isOpen} onClose={onClose} />

      {/* Vehicle View Modal */}
      <VehicleViewModal isOpen={isViewOpen} onClose={onViewClose} vehicle={selectedVehicle} />

      {/* Vehicle Edit Modal */}
      <VehicleEditModal isOpen={isEditOpen} onClose={onEditClose} vehicle={selectedVehicle} />

      {/* Vehicle Delete Dialog */}
      <VehicleDeleteDialog 
        isOpen={isDeleteOpen} 
        onClose={onDeleteClose} 
        onConfirm={confirmDeleteVehicle}
        vehicleId={vehicleToDelete}
      />
    </VStack>
  )
}
